import React from "react";
import { Link } from "react-router-dom";
import {
    Handshake,
    Award,
    Leaf,
    Lightbulb,
    Heart,
    Eye,
    ArrowRight,
    ArrowUpRight,
    Newspaper,
} from "lucide-react";
import SEO, { breadcrumbSchema, organizationSchema } from "@/components/seo/SEO";
import { REGISTRATION_URL, SITE } from "@/lib/config";
import api from "@/lib/api";
import useFreshData from "@/lib/useFreshData";

const formatPressDate = (iso) => {
    try {
        return new Intl.DateTimeFormat("es-ES", {
            day: "numeric",
            month: "long",
            year: "numeric",
        }).format(new Date(`${iso}T12:00:00`));
    } catch {
        return iso;
    }
};

const VALUES = [
    {
        icon: Handshake,
        title: "Colaboración",
        desc: "Trabajamos juntos —alojamientos, administraciones y entidades— porque el destino se construye entre todos.",
    },
    {
        icon: Award,
        title: "Profesionalidad",
        desc: "Impulsamos estándares de calidad, formación continua y buenas prácticas en el sector del alojamiento.",
    },
    {
        icon: Leaf,
        title: "Sostenibilidad",
        desc: "Promovemos un turismo responsable con el entorno natural, el patrimonio y la comunidad local de Tarifa.",
    },
    {
        icon: Lightbulb,
        title: "Innovación",
        desc: "Apostamos por la digitalización, nuevas tecnologías y modelos de gestión que hacen mejor destino.",
    },
    {
        icon: Heart,
        title: "Compromiso",
        desc: "Defendemos los intereses del sector con constancia, transparencia y vocación de largo plazo.",
    },
    {
        icon: Eye,
        title: "Transparencia",
        desc: "Actuamos con claridad en la gestión, la comunicación y la toma de decisiones: cuentas abiertas, procesos accesibles y rendición honesta a los asociados.",
    },
];

const WHAT_WE_DO = [
    "Representación institucional del sector del alojamiento turístico de Tarifa ante administraciones y organismos públicos.",
    "Defensa de los intereses colectivos de los asociados frente a normativas, tasas y decisiones que afecten al sector.",
    "Interlocución con el Ayuntamiento de Tarifa, la Junta de Andalucía y otras entidades competentes en materia turística.",
    "Promoción del destino Tarifa como referente de turismo de calidad, sostenible y desestacionalizado.",
    "Impulso de proyectos comunes de marketing, comunicación y visibilidad para los alojamientos asociados.",
    "Formación continua y transferencia de conocimiento en revenue management, atención al cliente y digitalización.",
    "Fomento de la sostenibilidad ambiental, la eficiencia energética y las buenas prácticas medioambientales.",
    "Creación de redes de colaboración entre alojamientos, empresas de servicios y entidades del sector.",
    "Organización de encuentros profesionales, jornadas y eventos de networking para el sector turístico local.",
];

export default function Institutional() {
    const { data: pressReleases } = useFreshData(
        async () => (await api.get("/press")).data,
        { initialData: [] }
    );

    return (
        <div data-testid="institutional-page" className="bg-white">
            <SEO
                title="Institucional · Quiénes somos, misión, visión y valores"
                description="Conoce ASATTA: nuestra misión, visión, valores y líneas de actuación. Más de 40 alojamientos turísticos unidos para construir el futuro del turismo en Tarifa."
                path="/institucional"
                jsonLd={[
                    organizationSchema,
                    breadcrumbSchema([
                        { name: "Inicio", path: "/" },
                        { name: "Institucional", path: "/institucional" },
                    ]),
                ]}
            />

            {/* Hero */}
            <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-16">
                <div className="text-xs uppercase tracking-widest text-[#082050]/70 mb-4">
                    Institucional · ASATTA
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#082050] tracking-tight leading-[1.05]">
                    Asociación de Alojamientos Turísticos de Tarifa.
                    <span className="block text-[#082050]/60 mt-4">
                        Unidos por un mismo objetivo: construir el futuro del turismo en Tarifa.
                    </span>
                </h1>
                <p className="mt-8 text-lg text-[#4A5568] max-w-3xl leading-relaxed">
                    ASATTA es la entidad que representa al sector del alojamiento
                    turístico de Tarifa. Agrupa a hoteles, hostales, apartamentos
                    turísticos, alojamientos rurales y otros establecimientos, sumando
                    más de <span className="text-[#082050] font-medium">40 asociados</span>{" "}
                    comprometidos con el desarrollo de un destino turístico de calidad,
                    sostenible y auténtico.
                </p>
            </section>

            {/* Misión */}
            <section className="bg-[#F8F9FA] border-y border-[#E2E4E9]">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 grid grid-cols-1 md:grid-cols-12 gap-10">
                    <div className="md:col-span-4">
                        <div className="text-xs uppercase tracking-widest text-[#082050]/70">
                            01 · Nuestra misión
                        </div>
                        <h2 className="mt-4 text-3xl sm:text-4xl font-semibold text-[#082050] tracking-tight">
                            Representar, defender y fortalecer.
                        </h2>
                    </div>
                    <div className="md:col-span-8">
                        <p className="text-lg text-[#4A5568] leading-relaxed">
                            Representar, defender y fortalecer al sector del alojamiento
                            turístico de Tarifa, promoviendo la cooperación entre
                            empresas, administraciones públicas y entidades colaboradoras
                            para consolidar un modelo turístico responsable, competitivo
                            y sostenible en el tiempo.
                        </p>
                    </div>
                </div>
            </section>

            {/* Visión */}
            <section>
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 grid grid-cols-1 md:grid-cols-12 gap-10">
                    <div className="md:col-span-4">
                        <div className="text-xs uppercase tracking-widest text-[#082050]/70">
                            02 · Nuestra visión
                        </div>
                        <h2 className="mt-4 text-3xl sm:text-4xl font-semibold text-[#082050] tracking-tight">
                            Tarifa, referente internacional.
                        </h2>
                    </div>
                    <div className="md:col-span-8">
                        <p className="text-lg text-[#4A5568] leading-relaxed">
                            Aspiramos a que Tarifa sea reconocida internacionalmente como
                            un destino turístico de calidad, sostenible, innovador y
                            auténtico, donde el visitante viva una experiencia excepcional
                            y donde el sector del alojamiento sea motor de desarrollo
                            económico, social y cultural del territorio.
                        </p>
                    </div>
                </div>
            </section>

            {/* Valores */}
            <section className="bg-[#082050] text-white">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
                    <div className="max-w-2xl mb-14">
                        <div className="text-xs uppercase tracking-widest text-white/60">
                            03 · Nuestros valores
                        </div>
                        <h2 className="mt-4 text-3xl sm:text-4xl font-semibold tracking-tight text-white">
                            Seis principios que guían cada decisión.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
                        {VALUES.map((v, i) => (
                            <div
                                key={v.title}
                                data-testid={`value-card-${i}`}
                                className="bg-[#082050] p-8"
                            >
                                <v.icon size={26} strokeWidth={1.5} className="text-white/80" />
                                <h3 className="mt-6 text-lg font-semibold text-white tracking-tight">
                                    {v.title}
                                </h3>
                                <p className="mt-3 text-sm text-white/70 leading-relaxed">
                                    {v.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Qué hacemos */}
            <section>
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
                    <div className="max-w-2xl mb-12">
                        <div className="text-xs uppercase tracking-widest text-[#082050]/70">
                            04 · Qué hacemos
                        </div>
                        <h2 className="mt-4 text-3xl sm:text-4xl font-semibold text-[#082050] tracking-tight">
                            Nuestras líneas de actuación.
                        </h2>
                        <p className="mt-4 text-[#4A5568] text-lg">
                            Nueve compromisos concretos que se traducen en acciones diarias
                            al servicio del sector.
                        </p>
                    </div>
                    <ol className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
                        {WHAT_WE_DO.map((item, i) => (
                            <li
                                key={i}
                                data-testid={`whatwedo-item-${i}`}
                                className="flex gap-4 border-t border-[#E2E4E9] pt-6"
                            >
                                <div className="text-xs font-semibold text-[#082050] tracking-widest w-8 shrink-0">
                                    {String(i + 1).padStart(2, "0")}
                                </div>
                                <p className="text-sm text-[#4A5568] leading-relaxed">
                                    {item}
                                </p>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            {/* Nuestro compromiso */}
            <section className="bg-[#F8F9FA] border-y border-[#E2E4E9]">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 grid grid-cols-1 md:grid-cols-12 gap-10">
                    <div className="md:col-span-4">
                        <div className="text-xs uppercase tracking-widest text-[#082050]/70">
                            05 · Nuestro compromiso
                        </div>
                        <h2 className="mt-4 text-3xl sm:text-4xl font-semibold text-[#082050] tracking-tight">
                            Un modelo turístico para el futuro.
                        </h2>
                    </div>
                    <div className="md:col-span-8 space-y-5 text-[#4A5568] text-base leading-relaxed">
                        <p>
                            En ASATTA nos comprometemos a trabajar cada día para que el
                            alojamiento turístico de Tarifa sea sinónimo de calidad,
                            hospitalidad y sostenibilidad. Creemos en un turismo
                            equilibrado, respetuoso con el entorno y con las personas que
                            hacen posible el destino.
                        </p>
                        <p>
                            Nuestro compromiso pasa por cuidar la excelencia del servicio,
                            fomentar la formación de los profesionales, impulsar la
                            digitalización de los alojamientos y colaborar con
                            administraciones y entidades para construir políticas
                            turísticas coherentes y a largo plazo.
                        </p>
                        <p>
                            Trabajamos con la mirada puesta en las próximas décadas: un
                            Tarifa competitivo internacionalmente pero fiel a su
                            identidad, un destino que sea al mismo tiempo motor económico
                            y refugio de autenticidad.
                        </p>
                    </div>
                </div>
            </section>

            {/* Tarifa 2035 */}
            <section>
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
                    <div className="max-w-3xl">
                        <div className="text-xs uppercase tracking-widest text-[#082050]/70">
                            06 · Iniciativa Tarifa 2035
                        </div>
                        <h2 className="mt-4 text-3xl sm:text-4xl font-semibold text-[#082050] tracking-tight">
                            Diseñando el Tarifa turístico de 2035.
                        </h2>
                        <p className="mt-6 text-[#4A5568] text-lg leading-relaxed">
                            Tarifa 2035 es la iniciativa estratégica de ASATTA para
                            imaginar y planificar, junto al resto de actores del
                            territorio, cómo queremos que sea nuestro destino en la
                            próxima década: un Tarifa reconocido internacionalmente,
                            desestacionalizado, sostenible y con un tejido de
                            alojamientos profesionales y comprometidos.
                        </p>
                    </div>

                    <div className="mt-12 border border-[#E2E4E9] p-8 sm:p-10 bg-white">
                        <div className="text-xs uppercase tracking-widest text-[#082050]/70 mb-3">
                            I Jornada Profesional & Networking
                        </div>
                        <h3 className="text-2xl sm:text-3xl font-semibold text-[#082050] tracking-tight">
                            Primer encuentro Tarifa 2035.
                        </h3>
                        <p className="mt-5 text-[#4A5568] leading-relaxed">
                            En el marco de la iniciativa Tarifa 2035, ASATTA organiza la I
                            Jornada Profesional & Networking: un espacio de encuentro
                            entre alojamientos, empresas del sector, administraciones y
                            expertos para debatir sobre el futuro del turismo en Tarifa,
                            compartir buenas prácticas y establecer alianzas estratégicas.
                        </p>
                        <p className="mt-4 text-[#4A5568] leading-relaxed">
                            La jornada combina mesas redondas, presentaciones de proyectos
                            y espacios de networking pensados para conectar a quienes
                            construyen día a día el destino turístico.
                        </p>
                    </div>
                </div>
            </section>

            {/* Actualidad Institucional */}
            <section
                id="actualidad"
                data-testid="institutional-press"
                className="bg-[#F8F9FA] border-y border-[#E2E4E9]"
            >
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
                    <div className="max-w-2xl mb-12">
                        <div className="text-xs uppercase tracking-widest text-[#082050]/70 flex items-center gap-2">
                            <Newspaper size={14} strokeWidth={1.75} />
                            Actualidad Institucional
                        </div>
                        <h2 className="mt-4 text-3xl sm:text-4xl font-semibold text-[#082050] tracking-tight">
                            Notas de prensa y noticias oficiales.
                        </h2>
                        <p className="mt-4 text-[#4A5568] text-lg">
                            Comunicados oficiales de la asociación: reuniones
                            institucionales, acuerdos y avances en defensa del
                            sector del alojamiento en Tarifa.
                        </p>
                    </div>

                    {pressReleases.length === 0 ? (
                        <p className="text-sm text-[#718096]">
                            Próximamente publicaremos las notas de prensa de la
                            asociación.
                        </p>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {pressReleases.map((p) => (
                                <Link
                                    key={p.slug}
                                    to={`/institucional/actualidad/${p.slug}`}
                                    data-testid={`press-card-${p.slug}`}
                                    className="group bg-white border border-[#E2E4E9] hover:border-[#082050] transition-colors flex flex-col"
                                >
                                    {p.images && p.images[0] && (
                                        <img
                                            src={p.images[0]}
                                            alt={`Imagen de la nota: ${p.title}`}
                                            className="w-full aspect-[16/9] object-cover"
                                            loading="lazy"
                                        />
                                    )}
                                    <div className="p-7 flex flex-col flex-1">
                                        <div className="text-[11px] uppercase tracking-widest text-[#718096]">
                                            Nota de prensa ·{" "}
                                            {formatPressDate(p.date)}
                                        </div>
                                        <h3 className="mt-3 text-xl font-semibold text-[#082050] tracking-tight leading-snug">
                                            {p.title}
                                        </h3>
                                        <p className="mt-3 text-sm text-[#4A5568] leading-relaxed flex-1">
                                            {p.excerpt}
                                        </p>
                                        <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#082050] group-hover:gap-3 transition-all">
                                            Leer nota completa
                                            <ArrowUpRight size={15} />
                                        </span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Frase final destacada */}
            <section className="bg-[#082050] text-white">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 text-center">
                    <p className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight leading-[1.2] text-white">
                        “No representamos únicamente a los alojamientos turísticos;
                        trabajamos para fortalecer el destino al que pertenecemos.”
                    </p>
                    <div className="mt-8 text-xs uppercase tracking-widest text-white/60">
                        — ASATTA
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="border-t border-[#E2E4E9] bg-white">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-[#082050]/70 mb-3">
                            ¿Gestionas un alojamiento en Tarifa?
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-semibold text-[#082050] tracking-tight">
                            Súmate a la asociación.
                        </h2>
                        <p className="mt-4 text-[#4A5568]">
                            Únete a ASATTA por {SITE.quota} y participa en los proyectos
                            que están construyendo el futuro del turismo en Tarifa.
                        </p>
                    </div>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                        <a
                            href={REGISTRATION_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="btn-inst-hazte-socio"
                            className="inline-flex items-center gap-2 bg-[#082050] text-white px-6 py-3 text-sm font-medium hover:bg-[#082050]/90"
                        >
                            Hazte socio
                            <ArrowRight size={14} />
                        </a>
                        <Link
                            to="/contacto"
                            data-testid="btn-inst-contact"
                            className="inline-flex items-center gap-2 border border-[#082050] text-[#082050] px-6 py-3 text-sm font-medium hover:bg-[#082050]/5"
                        >
                            Contactar
                            <ArrowRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
